import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardShell from "../components/DashboardShell";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Calendar, MapPin, Video, ArrowLeft, Megaphone, Users as UsersIcon, X, Send } from "lucide-react";

const TABS = ["Details", "Attendees", "Discussion"];

export default function EventDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Details");
  const [evt, setEvt] = useState(null);
  const [attendees, setAttendees] = useState({ going: [], interested: [], not_going: [] });
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [showBlast, setShowBlast] = useState(false);
  const [blast, setBlast] = useState({ subject: "", body: "", audience: "all" });

  const load = async () => {
    try {
      const [er, ar, cr] = await Promise.all([
        api.get(`/events/${id}`),
        api.get(`/events/${id}/attendees`).catch(() => ({ data: { going: [], interested: [], not_going: [] } })),
        api.get(`/events/${id}/comments`).catch(() => ({ data: { items: [] } })),
      ]);
      setEvt(er.data); setAttendees(ar.data); setComments(cr.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const rsvp = async (s) => {
    try { await api.post(`/events/${id}/rsvp?status=${s}`); toast.success(`RSVP set: ${s.replace("_"," ")}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const comment = async () => {
    if (!text.trim()) return;
    try { await api.post(`/events/${id}/comments`, { content: text }); setText(""); toast.success("Comment posted"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this event?")) return;
    try { await api.delete(`/events/${id}`); toast.success("Event cancelled"); navigate("/events"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const sendBlast = async () => {
    try { const { data } = await api.post(`/events/${id}/message-rsvps`, blast); toast.success(`Sent to ${data.sent}`); setShowBlast(false); setBlast({ subject:"", body:"", audience:"all" }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!evt) return <DashboardShell><div className="text-[#94a3b8] text-sm" data-testid="event-loading">Loading event…</div></DashboardShell>;

  const isCreator = evt.creator_id === user?.id || user?.role === "admin";

  return (
    <DashboardShell>
      <div className="space-y-5" data-testid="event-detail-page">
        <Link to="/events" className="csc-btn-ghost !py-1.5 inline-flex items-center gap-1" data-testid="back-to-events"><ArrowLeft size={12} /> Events</Link>

        <div className="csc-card p-6 space-y-3">
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// Event · {evt.category}</div>
          <h1 className="text-2xl lg:text-3xl font-semibold">{evt.name}</h1>
          {evt.cancelled && <div className="csc-card p-2 text-xs text-red-400 inline-block">EVENT CANCELLED</div>}
          <p className="text-sm text-[#94a3b8] whitespace-pre-wrap">{evt.description}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono-tech text-[#94a3b8]">
            <div className="flex items-center gap-2"><Calendar size={12} className="text-[#00aaff]" /> {new Date(evt.starts_at).toLocaleString()} → {new Date(evt.ends_at).toLocaleString()}</div>
            <div className="flex items-center gap-2">{evt.is_online ? <Video size={12} className="text-[#00aaff]" /> : <MapPin size={12} />} {evt.is_online ? (evt.url || "Online") : (evt.location || "TBD")}</div>
          </div>

          {!evt.cancelled && (
            <div className="flex flex-wrap gap-2">
              {["going","interested","not_going"].map((s) => (
                <button key={s} onClick={() => rsvp(s)} className={`csc-pill ${evt.rsvp === s ? "csc-pill-active" : ""}`} data-testid={`event-rsvp-${s}`}>{s.replace("_"," ")}</button>
              ))}
            </div>
          )}
          <div className="text-[11px] font-mono-tech text-[#94a3b8]">{evt.going} going · {evt.interested} interested · {evt.not_going} not going</div>

          {isCreator && !evt.cancelled && (
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowBlast(true)} className="csc-btn-ghost !py-1.5" data-testid="event-message-rsvps"><Megaphone size={12} /> Message RSVPs</button>
              <button onClick={cancel} className="csc-btn-ghost !py-1.5 hover:!border-red-400 hover:!text-red-400" data-testid="event-cancel-btn">Cancel event</button>
            </div>
          )}
        </div>

        {showBlast && (
          <div className="csc-card p-5 space-y-3" data-testid="blast-modal">
            <div className="flex items-center justify-between"><h3 className="font-semibold">Message RSVPs</h3><button onClick={() => setShowBlast(false)} className="csc-btn-ghost !p-1.5"><X size={12} /></button></div>
            <select value={blast.audience} onChange={(e) => setBlast({...blast, audience:e.target.value})} className="csc-input" data-testid="blast-audience">
              <option value="all">All RSVPs</option><option value="going">Going only</option><option value="interested">Interested only</option>
            </select>
            <input value={blast.subject} onChange={(e) => setBlast({...blast, subject:e.target.value})} placeholder="Subject" className="csc-input" data-testid="blast-subject" />
            <textarea value={blast.body} onChange={(e) => setBlast({...blast, body:e.target.value})} placeholder="Message" rows={4} className="csc-input resize-none" data-testid="blast-body" />
            <button onClick={sendBlast} className="csc-btn-primary" data-testid="blast-send">Send</button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 border-b border-[rgba(0,170,255,0.18)] pb-1">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-xs font-mono-tech uppercase tracking-wider border-b-2 ${tab===t?"border-[#00aaff] text-[#00aaff]":"border-transparent text-[#94a3b8] hover:text-white"}`} data-testid={`event-tab-${t.toLowerCase()}`}>{t}</button>
          ))}
        </div>

        {tab === "Details" && (
          <div className="csc-card p-5 text-sm" data-testid="event-details">
            <p className="text-[#94a3b8] whitespace-pre-wrap">{evt.description || "No additional details."}</p>
            <div className="mt-3 text-[11px] font-mono-tech text-[#94a3b8]">Created by {evt.creator_name}</div>
          </div>
        )}

        {tab === "Attendees" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="event-attendees">
            {[
              ["going", "Going", attendees.going],
              ["interested", "Interested", attendees.interested],
              ["not_going", "Not going", attendees.not_going],
            ].map(([k, label, list]) => (
              <div key={k} className="csc-card p-4">
                <div className="flex items-center gap-2 text-xs font-mono-tech text-[#94a3b8] mb-2"><UsersIcon size={12} className="text-[#00aaff]" /> {label} ({list.length})</div>
                {list.length === 0 && <div className="text-xs text-[#94a3b8]">None yet.</div>}
                {list.map((p) => (
                  <Link key={p.id} to={p.handle ? `/u/${p.handle}` : `/profile/${p.id}`} className="block text-sm py-1 hover:text-[#00aaff]" data-testid={`attendee-${k}-${p.id}`}>{p.name}</Link>
                ))}
              </div>
            ))}
          </div>
        )}

        {tab === "Discussion" && (
          <div className="space-y-3" data-testid="event-discussion">
            <div className="csc-card p-4 space-y-2">
              <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Discuss the event…" rows={2} className="csc-input resize-none" data-testid="event-comment-input" />
              <div className="flex justify-end"><button onClick={comment} className="csc-btn-primary" data-testid="event-comment-submit"><Send size={12} /> Post</button></div>
            </div>
            {comments.length === 0 && <div className="csc-card p-6 text-center text-[#94a3b8] text-sm" data-testid="event-discussion-empty">No comments yet.</div>}
            {comments.map((c) => (
              <div key={c.id} className="csc-card csc-overflow-safe p-3" data-testid={`event-comment-${c.id}`}>
                <div className="text-[11px] font-mono-tech text-[#94a3b8]">{c.author_name} · {new Date(c.created_at).toLocaleString()}</div>
                <div className="text-sm mt-1 csc-break-anywhere">{c.content}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
